import { Moment } from 'moment-timezone';
import { ReportEvaluation } from 'types/Evaluation';
import { Option } from 'types/Generics';
import { IntakeFormCustomQuestionAnswer } from 'types/IntakeForm';
import { v4 as uuid } from 'uuid';

export const emptyPet = (customerId: number): Pet => ({
    uuid: uuid(),
    id: 0,
    name: '',
    customerId,
    active: true,
    deleted: false,
    type: {} as PetType,
    size: {} as PetSize,
    gender: {} as PetGender,
    hairColor: {} as PetHairColor,
    hairLength: {} as PetHairLength,
    birthdate: null,
    vaccines: [],
    answers: [],
    positivePersonalityList: [],
    negativePersonalityList: []
});

export type SavePet = Omit<Pet, 'id'> & {
    id?: number;
};

export interface Pet {
    id: number;
    customerId: number;
    name: string;
    type: PetType;
    size: PetSize;
    breed?: PetBreed;
    gender?: PetGender;
    weight?: number;
    hairColor?: PetHairColor;
    birthdate: Moment | null;
    hairLength?: PetHairLength;
    image?: {
        file?: File;
        src: string;
        removed?: boolean;
    };
    uuid: string;
    active: boolean;
    deleted: boolean;
    vaccines: Array<PetVaccine>;
    answers: Array<IntakeFormCustomQuestionAnswer>;
    evaluationReport?: ReportEvaluation;
    positivePersonalityList: Array<string>;
    negativePersonalityList: Array<string>;
}

export const PetTypeIds = {
    DOG: 'dogs',
    CAT: 'cats',
    EXOTIC: 'exotics'
};

export type PetTypeId = (typeof PetTypeIds)[keyof typeof PetTypeIds];

export interface PetType {
    id: PetTypeId;
    singular: string;
    plural: string;
}

export type PetSize = Option<string> & {
    minWeight: number | null;
    maxWeight: number | null;
};

export type PetBreed = Option<number | string>;

export type PetGender = Option<string>;

export type PetHairLength = Option<string>;

export type PetHairColor = Option<string>;

export type PetVaccine = {
    id?: number;
    uuid: string;
    status: string;
    expiration: Moment | undefined;
    type: Option<string>;
    recordUrl: string | undefined;
    recordFileToUpload?: File;
};

export const createEmptyVaccine = (): PetVaccine => ({
    uuid: uuid(),
    status: 'active',
    expiration: undefined,
    recordUrl: undefined,
    recordFileToUpload: undefined,
    type: { id: '', name: '' }
});
